import { defineStore } from 'pinia'
import { computed } from 'vue'
import { Capacitor } from '@capacitor/core'
import type {
  INotificationPreferences,
  IPushNotificationPreferences,
  NotificationType,
  NotificationSoundPlatformKeys,
  NotificationSettingKey,
} from 'src/interfaces/notificationPreferences'
import { useUserConfigStore } from 'src/common/store'

export const useNotificationSettingsStore = defineStore(
  'notificationSettings',
  () => {
    const userConfigStore = useUserConfigStore()

    const IN_APP_PREFERENCES_KEY = 'notificationPreferences.inApp'
    const PUSH_PREFERENCES_KEY = 'notificationPreferences.push'

    const inAppPreferences = computed<INotificationPreferences | undefined>(
      () =>
        userConfigStore.get<INotificationPreferences>(IN_APP_PREFERENCES_KEY),
    )

    const pushPreferences = computed<IPushNotificationPreferences | undefined>(
      () =>
        userConfigStore.get<IPushNotificationPreferences>(PUSH_PREFERENCES_KEY),
    )

    const hasInAppPreferences = computed(
      () => inAppPreferences.value !== undefined,
    )

    const hasPushPreferences = computed(
      () => pushPreferences.value !== undefined,
    )

    function isInAppEnabled(type: NotificationType): boolean {
      if (!inAppPreferences.value?.preferences) {
        return true
      }

      const pref = inAppPreferences.value.preferences.find(
        (p) => p.notificationType === type,
      )

      return pref?.enabled ?? true
    }

    function isNotificationSettingEnabled(
      key: NotificationSettingKey,
      defaultEnabled = true,
    ): boolean {
      return userConfigStore.get<boolean>(key, defaultEnabled) ?? defaultEnabled
    }

    function isNotificationSoundEnabled(
      key: NotificationSettingKey,
      platformKeys?: NotificationSoundPlatformKeys,
      defaultEnabled = true,
    ): boolean {
      const soundEnabled = isNotificationSettingEnabled(key, defaultEnabled)
      if (!soundEnabled) {
        return false
      }

      if (!platformKeys) {
        return true
      }

      const platformKey = Capacitor.isNativePlatform()
        ? platformKeys.mobile
        : platformKeys.desktop

      return isNotificationSettingEnabled(platformKey, true)
    }

    function isPushEnabled(type: NotificationType): boolean {
      if (!pushPreferences.value?.preferences) {
        return true
      }

      const pref = pushPreferences.value.preferences.find(
        (p) => p.notificationType === type,
      )

      return pref?.enabled ?? true
    }

    function isPushEnabledForCenter(centerId?: string): boolean {
      if (!centerId || !pushPreferences.value) {
        return true
      }

      return !pushPreferences.value.disabledCenters?.includes(centerId)
    }

    function isInAppEnabledForCenter(
      type: NotificationType,
      centerId?: string,
    ): boolean {
      if (!centerId || !inAppPreferences.value?.preferences) {
        return isInAppEnabled(type)
      }

      const pref = inAppPreferences.value.preferences.find(
        (p) => p.notificationType === type,
      )

      if (!pref) {
        return true
      }

      if (!pref.centerIds || pref.centerIds.length === 0) {
        return pref.enabled
      }

      return pref.enabled && pref.centerIds.includes(centerId)
    }

    return {
      // Computed
      inAppPreferences,
      pushPreferences,
      hasInAppPreferences,
      hasPushPreferences,

      // Methods
      isInAppEnabled,
      isPushEnabled,
      isPushEnabledForCenter,
      isInAppEnabledForCenter,
      isNotificationSettingEnabled,
      isNotificationSoundEnabled,
    }
  },
)
